import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import type { Edge, Node } from "reactflow"

import { toast } from "@coval/ui"

import type { IVREdge, IVRFlow, IVRNode } from "@/types/IVRFlow"

import {
  addBlankNodeToFlow,
  addEdgeToFlow,
  connectNodesInFlow,
  deleteNodeFromFlow,
  removeEdgeFromFlow,
  renameNodeInFlow,
  renamePosition,
  setNodeKindInFlow,
  updateEdgeInFlow,
  updateNodePropsInFlow,
  type NodeKind,
} from "./ivr-flow-tree-ops"
import { flowToReactFlow, validateIVRFlow } from "./ivr-flow-utils"
import type { OutgoingEdgeEntry } from "./NodeEditor"

type NodePositions = Record<string, { x: number; y: number }>

interface ValidationResult {
  valid: boolean
  issues: string[]
}

interface IVRFlowStateApi {
  flow: IVRFlow | null
  selectedNodeId: string | null
  setSelectedNodeId: (id: string | null) => void
  validation: ValidationResult
  reactFlowData: { nodes: Node[]; edges: Edge[] }
  selectedNode: IVRNode | null
  selectedOutgoingEdges: OutgoingEdgeEntry[]
  replaceFlow: (next: IVRFlow) => void
  clearFlow: () => void
  updateNodeProps: (nodeId: string, patch: Partial<IVRNode>) => void
  setNodeKind: (nodeId: string, kind: NodeKind) => void
  addEdgeForSource: (sourceId: string, destinationId?: string) => void
  updateEdge: (index: number, patch: Partial<IVREdge>) => void
  removeEdge: (index: number) => void
  handleNodeConnect: (sourceId: string, targetId: string) => void
  handleNodePositionChange: (
    id: string,
    position: { x: number; y: number },
  ) => void
  addBlankNode: () => void
  deleteNode: (nodeId: string) => void
  setStartNode: (nodeId: string) => void
  renameNode: (oldId: string, nextId: string) => boolean
}

/**
 * Owns the editable IVR flow and everything derived from or mutating it: the
 * current flow, per-node canvas positions, the selected node, validation, and
 * the full set of structural mutators. Mutators delegate to the pure tree-ops
 * helpers so the graph logic stays unit-testable.
 *
 * `onFlowChange` fires on every flow mutation. It is held in a ref so an
 * unstable callback identity from the parent can't create an infinite loop
 * (parent gets the new flow → sets state → re-renders with a fresh callback →
 * would otherwise re-fire the effect).
 */
export const useIVRFlowState = (
  initialFlow?: IVRFlow | null,
  onFlowChange?: (flow: IVRFlow | null) => void,
): IVRFlowStateApi => {
  const [flow, setFlow] = useState<IVRFlow | null>(initialFlow ?? null)
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null)
  const [nodePositions, setNodePositions] = useState<NodePositions>({})

  useEffect(() => {
    if (initialFlow !== undefined) {
      setFlow(initialFlow ?? null)
    }
  }, [initialFlow])

  const onFlowChangeRef = useRef(onFlowChange)
  useEffect(() => {
    onFlowChangeRef.current = onFlowChange
  }, [onFlowChange])
  useEffect(() => {
    onFlowChangeRef.current?.(flow)
  }, [flow])

  const validation = useMemo<ValidationResult>(
    () => (flow ? validateIVRFlow(flow) : { valid: true, issues: [] }),
    [flow],
  )

  const reactFlowData = useMemo(
    () =>
      flow ? flowToReactFlow(flow, nodePositions) : { nodes: [], edges: [] },
    [flow, nodePositions],
  )

  const selectedNode = useMemo(
    () =>
      flow && selectedNodeId
        ? (flow.nodes.find((n) => n.id === selectedNodeId) ?? null)
        : null,
    [flow, selectedNodeId],
  )

  const selectedOutgoingEdges = useMemo<OutgoingEdgeEntry[]>(() => {
    if (!flow || !selectedNodeId) {
      return []
    }
    return flow.edges
      .map((edge, index) => ({ edge, index }))
      .filter(({ edge }) => edge.source === selectedNodeId)
  }, [flow, selectedNodeId])

  const replaceFlow = useCallback((next: IVRFlow) => {
    setFlow(next)
    setNodePositions({})
    setSelectedNodeId(null)
  }, [])

  const clearFlow = useCallback(() => {
    setFlow(null)
    setSelectedNodeId(null)
    setNodePositions({})
  }, [])

  const updateNodeProps = useCallback(
    (nodeId: string, patch: Partial<IVRNode>) => {
      setFlow((prev) =>
        prev ? updateNodePropsInFlow(prev, nodeId, patch) : prev,
      )
    },
    [],
  )

  const setNodeKind = useCallback((nodeId: string, kind: NodeKind) => {
    setFlow((prev) => (prev ? setNodeKindInFlow(prev, nodeId, kind) : prev))
  }, [])

  const addEdgeForSource = useCallback(
    (sourceId: string, destinationId?: string) => {
      setFlow((prev) =>
        prev ? addEdgeToFlow(prev, sourceId, destinationId) : prev,
      )
    },
    [],
  )

  const updateEdge = useCallback((index: number, patch: Partial<IVREdge>) => {
    setFlow((prev) => (prev ? updateEdgeInFlow(prev, index, patch) : prev))
  }, [])

  const removeEdge = useCallback((index: number) => {
    setFlow((prev) => (prev ? removeEdgeFromFlow(prev, index) : prev))
  }, [])

  const handleNodeConnect = useCallback(
    (sourceId: string, targetId: string) => {
      if (sourceId === targetId) {
        return
      }
      setFlow((prev) =>
        prev ? connectNodesInFlow(prev, sourceId, targetId) : prev,
      )
      setSelectedNodeId(sourceId)
    },
    [],
  )

  const handleNodePositionChange = useCallback(
    (id: string, position: { x: number; y: number }) => {
      setNodePositions((prev) => ({ ...prev, [id]: position }))
    },
    [],
  )

  const addBlankNode = useCallback(() => {
    setFlow((prev) => {
      const { flow: nextFlow, nodeId } = addBlankNodeToFlow(prev)
      setSelectedNodeId(nodeId)
      return nextFlow
    })
  }, [])

  const deleteNode = useCallback((nodeId: string) => {
    setFlow((prev) => (prev ? deleteNodeFromFlow(prev, nodeId) : prev))
    setSelectedNodeId((prev) => (prev === nodeId ? null : prev))
    setNodePositions((prev) => {
      const next = { ...prev }
      delete next[nodeId]
      return next
    })
  }, [])

  const setStartNode = useCallback((nodeId: string) => {
    setFlow((prev) => (prev ? { ...prev, start_node: nodeId } : prev))
  }, [])

  const renameNode = useCallback(
    (oldId: string, nextId: string): boolean => {
      const trimmed = nextId.trim()
      if (!flow || !trimmed || trimmed === oldId) {
        return false
      }
      if (flow.nodes.some((n) => n.id === trimmed)) {
        toast.danger({
          title: "Cannot rename node",
          description: `A node with id "${trimmed}" already exists.`,
        })
        return false
      }
      setFlow((prev) => (prev ? renameNodeInFlow(prev, oldId, trimmed) : prev))
      setSelectedNodeId((prev) => (prev === oldId ? trimmed : prev))
      setNodePositions((prev) => renamePosition(prev, oldId, trimmed))
      return true
    },
    [flow],
  )

  return {
    flow,
    selectedNodeId,
    setSelectedNodeId,
    validation,
    reactFlowData,
    selectedNode,
    selectedOutgoingEdges,
    replaceFlow,
    clearFlow,
    updateNodeProps,
    setNodeKind,
    addEdgeForSource,
    updateEdge,
    removeEdge,
    handleNodeConnect,
    handleNodePositionChange,
    addBlankNode,
    deleteNode,
    setStartNode,
    renameNode,
  }
}
